import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Grid,
  Icon,
  IconButton,
  Switch,
  Tooltip,
  Checkbox,
  Chip,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Autocomplete,
  Select,
  MenuItem
} from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import { useMaterialUIController } from "context";
import { get, post } from "utils/WebService";

export default function MarcacaoContatosLoteConsult() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Aba ativa: 0 = Telefones, 1 = E-mails
  const [tabIndex, setTabIndex] = useState(0);

  // Estados de dados e paginação
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [data, setData] = useState({
    totalEncontrados: 0,
    totalAtivos: 0,
    totalInvalidos: 0,
    totalBlacklist: 0,
    itens: [],
    page: 0,
    size: 50,
    totalPages: 0,
  });

  // Filtros
  const [filtroCliente, setFiltroCliente] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("TODOS");
  const [filtroTipoTelefone, setFiltroTipoTelefone] = useState("");
  const [filtroOrigem, setFiltroOrigem] = useState("");
  const [filtroDataInicio, setFiltroDataInicio] = useState("");
  const [filtroDataFim, setFiltroDataFim] = useState("");
  const [filtroBusca, setFiltroBusca] = useState("");

  // Seleção em lote
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllResults, setSelectAllResults] = useState(false);

  // Painel de Ação
  const [acaoSelecionada, setAcaoSelecionada] = useState("INVALIDO");
  const [observacao, setObservacao] = useState("");
  const [applyingAction, setApplyingAction] = useState(false);

  // Histórico
  const [historicoLogs, setHistoricoLogs] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [detalhesModalOpen, setDetalhesModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Notificações
  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "info",
    title: "",
    content: "",
  });

  const showSnackbar = (color, title, content) => {
    setSnackbar({ open: true, color, title, content });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Obter usuário logado
  const usuarioLogadoNome = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.ccNome || u.ccLogin || "Supervisor";
    } catch (e) {
      return "Supervisor";
    }
  }, []);

  // Carregar lista de clientes para o filtro
  useEffect(() => {
    get("/pessoa/list-all")
      .then((res) => {
        setClientes(res?.list || []);
      })
      .catch((err) => {
        console.error("Erro ao carregar clientes:", err);
      });
  }, []);

  // Busca principal
  const fetchData = useCallback(
    (page = 0, currentSize = data.size) => {
      setLoading(true);
      const isEmail = tabIndex === 1;
      const url = isEmail
        ? "/marcacao-contatos/emails/search"
        : "/marcacao-contatos/telefones/search";

      const payload = {
        clienteId: filtroCliente ? filtroCliente.id : null,
        status: filtroStatus,
        tipoTelefone: filtroTipoTelefone !== "" ? parseInt(filtroTipoTelefone, 10) : null,
        origem: filtroOrigem !== "" ? parseInt(filtroOrigem, 10) : null,
        dataInicio: filtroDataInicio || null,
        dataFim: filtroDataFim || null,
        busca: filtroBusca || null,
        page,
        size: currentSize,
      };

      post(url, payload)
        .then((res) => {
          setData({
            totalEncontrados: res?.totalEncontrados || 0,
            totalAtivos: res?.totalAtivos || 0,
            totalInvalidos: res?.totalInvalidos || 0,
            totalBlacklist: res?.totalBlacklist || 0,
            itens: res?.itens || [],
            page: res?.page || 0,
            size: res?.size || currentSize,
            totalPages: res?.totalPages || 0,
          });
        })
        .catch((err) => {
          console.error("Erro ao buscar contatos:", err);
          showSnackbar("error", "Erro na Busca", err?.message || "Erro ao consultar contatos.");
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      tabIndex,
      filtroCliente,
      filtroStatus,
      filtroTipoTelefone,
      filtroOrigem,
      filtroDataInicio,
      filtroDataFim,
      filtroBusca,
      data.size,
    ]
  );

  // Carregar dados sempre que trocar de aba
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectAllResults(false);
    fetchData(0);
  }, [tabIndex]);

  // Carregar histórico
  const fetchHistorico = useCallback(() => {
    setLoadingHistorico(true);
    get("/marcacao-contatos/historico")
      .then((res) => {
        setHistoricoLogs(Array.isArray(res) ? res : []);
      })
      .catch((err) => {
        console.error("Erro ao buscar histórico:", err);
      })
      .finally(() => {
        setLoadingHistorico(false);
      });
  }, []);

  useEffect(() => {
    fetchHistorico();
  }, [fetchHistorico]);

  // Manipulação de seleção
  const handleToggleSelect = (id) => {
    setSelectAllResults(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectPage = (checked) => {
    setSelectAllResults(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      data.itens.forEach((item) => {
        if (checked) {
          next.add(item.id);
        } else {
          next.delete(item.id);
        }
      });
      return next;
    });
  };

  const isPageAllSelected = useMemo(() => {
    if (!data.itens || data.itens.length === 0) return false;
    return data.itens.every((item) => selectedIds.has(item.id));
  }, [data.itens, selectedIds]);

  const handleSelectAllFiltered = () => {
    setSelectAllResults(true);
    const allPageIds = new Set(data.itens.map((i) => i.id));
    setSelectedIds(allPageIds);
    showSnackbar(
      "info",
      "Seleção Global Ativada",
      `Todos os ${data.totalEncontrados.toLocaleString()} registros do filtro atual serão processados.`
    );
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllResults(false);
  };

  const totalSelecionadosCount = selectAllResults ? data.totalEncontrados : selectedIds.size;

  // Executar marcação em lote
  const handleExecuteAction = () => {
    if (totalSelecionadosCount === 0) {
      showSnackbar("warning", "Atenção", "Selecione ao menos um contato para aplicar a ação.");
      return;
    }

    setApplyingAction(true);
    const isEmail = tabIndex === 1;

    const payload = {
      tipoContato: isEmail ? "EMAIL" : "TELEFONE",
      acao: acaoSelecionada,
      ids: Array.from(selectedIds),
      todosResultados: selectAllResults,
      filtro: {
        clienteId: filtroCliente ? filtroCliente.id : null,
        status: filtroStatus,
        tipoTelefone: filtroTipoTelefone !== "" ? parseInt(filtroTipoTelefone, 10) : null,
        origem: filtroOrigem !== "" ? parseInt(filtroOrigem, 10) : null,
        dataInicio: filtroDataInicio || null,
        dataFim: filtroDataFim || null,
        busca: filtroBusca || null,
      },
      observacao,
    };

    post("/marcacao-contatos/executar", payload)
      .then((res) => {
        showSnackbar(
          "success",
          "Marcação Aplicada!",
          `Ação processada com sucesso: ${res?.qtdeMarcada || 0} contatos marcados.`
        );
        setSelectedIds(new Set());
        setSelectAllResults(false);
        setObservacao("");
        fetchData(data.page);
        fetchHistorico();
      })
      .catch((err) => {
        console.error("Erro ao aplicar marcação:", err);
        showSnackbar("error", "Erro ao Processar", err?.message || "Falha ao executar marcação em lote.");
      })
      .finally(() => {
        setApplyingAction(false);
      });
  };

  // Ações de filtro
  const handleAplicarFiltro = (e) => {
    if (e) e.preventDefault();
    setSelectedIds(new Set());
    setSelectAllResults(false);
    fetchData(0);
  };

  const handleLimparFiltros = () => {
    setFiltroCliente(null);
    setFiltroStatus("TODOS");
    setFiltroTipoTelefone("");
    setFiltroOrigem("");
    setFiltroDataInicio("");
    setFiltroDataFim("");
    setFiltroBusca("");
    setSelectedIds(new Set());
    setSelectAllResults(false);
  };

  // Cores dark mode padrão do workspace
  const cardBg = darkMode ? "#4B4F5C" : "#ffffff";
  const innerCardBg = darkMode ? "#424550" : "#f8f9fa";
  const borderColor = darkMode ? "rgba(255, 255, 255, 0.18)" : "#e2e8f0";
  const inputBg = darkMode ? "#5A5F6E" : "#ffffff";
  const inputBorder = darkMode ? "rgba(255, 255, 255, 0.4)" : "#ced4da";
  const inputColor = darkMode ? "#ffffff !important" : "inherit";

  const commonInputSx = {
    backgroundColor: inputBg,
    borderRadius: "8px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      color: inputColor,
      "& fieldset": {
        borderColor: `${inputBorder} !important`,
      },
      "&:hover fieldset": {
        borderColor: `${darkMode ? "#90caf9" : "#2196f3"} !important`,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#2196f3 !important",
      },
    },
    "& .MuiInputBase-input": {
      color: inputColor,
    },
    "& .MuiInputBase-input::placeholder": {
      color: darkMode ? "#c8cdd8 !important" : "#777",
      opacity: 1,
    },
  };

  const commonSelectSx = {
    height: 40,
    backgroundColor: inputBg,
    color: inputColor,
    borderRadius: "8px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: `${inputBorder} !important`,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${darkMode ? "#90caf9" : "#2196f3"} !important`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2196f3 !important",
    },
    "& .MuiSelect-select": { color: inputColor },
    "& .MuiSvgIcon-root": { color: inputColor },
  };

  return (
    <MDBox py={3} px={2}>
      {/* Barra de Abas Superiores (Telefones vs E-mails) */}
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="bold" color={darkMode ? "white" : "dark"}>
            Gestão de Contatos
          </MDTypography>
          <MDTypography variant="button" color="text" sx={{ fontSize: "0.85rem" }}>
            Selecione os filtros, visualize os contatos e marque em massa como Inválido ou Blacklist.
          </MDTypography>
        </MDBox>
        <Tabs
          value={tabIndex}
          onChange={(e, val) => setTabIndex(val)}
          sx={{
            backgroundColor: darkMode ? "#424550" : "#eaedf1",
            borderRadius: "10px",
            p: 0.5,
            "& .MuiTab-root": {
              minHeight: 36,
              py: 0.5,
              px: 2.5,
              fontSize: "0.85rem",
              fontWeight: "bold",
              borderRadius: "8px",
              color: darkMode ? "#c2c7d0" : "#555",
              "&.Mui-selected": {
                backgroundColor: darkMode ? "#1976d2" : "#ffffff",
                color: darkMode ? "#ffffff !important" : "#1976d2 !important",
                boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
              },
            },
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          <Tab icon={<Icon sx={{ mr: 1, fontSize: "1.1rem" }}>phone</Icon>} iconPosition="start" label="Telefones" />
          <Tab icon={<Icon sx={{ mr: 1, fontSize: "1.1rem" }}>email</Icon>} iconPosition="start" label="E-mails" />
        </Tabs>
      </MDBox>

      {/* 4 Cards de Indicadores (KPIs) */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: cardBg, p: 2, borderRadius: "12px", border: `1px solid ${borderColor}` }}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: darkMode ? "rgba(33, 150, 243, 0.2)" : "#e3f2fd",
                  color: "#1976d2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon fontSize="medium">{tabIndex === 0 ? "phone" : "email"}</Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                  {data.totalEncontrados.toLocaleString()}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {tabIndex === 0 ? "Telefones encontrados" : "E-mails encontrados"}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: cardBg, p: 2, borderRadius: "12px", border: `1px solid ${borderColor}` }}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: darkMode ? "rgba(103, 114, 229, 0.2)" : "#ede7f6",
                  color: "#5e35b1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon fontSize="medium">checklist</Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                  {totalSelecionadosCount.toLocaleString()}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {tabIndex === 0 ? "Telefones selecionados" : "E-mails selecionados"}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: cardBg, p: 2, borderRadius: "12px", border: `1px solid ${borderColor}` }}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: darkMode ? "rgba(255, 152, 0, 0.2)" : "#fff3e0",
                  color: "#f57c00",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon fontSize="medium">phone_disabled</Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                  {data.totalInvalidos.toLocaleString()}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Marcados como Inválidos
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: cardBg, p: 2, borderRadius: "12px", border: `1px solid ${borderColor}` }}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: darkMode ? "rgba(244, 67, 54, 0.2)" : "#ffebee",
                  color: "#d32f2f",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon fontSize="medium">block</Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                  {data.totalBlacklist.toLocaleString()}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Marcados como Blacklist
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros da Pesquisa (Card) */}
      <Card sx={{ backgroundColor: cardBg, p: 2.5, borderRadius: "12px", border: `1px solid ${borderColor}`, mb: 2.5 }}>
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <Icon color="info">tune</Icon>
          <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
            Filtros da Pesquisa
          </MDTypography>
        </MDBox>

        <form onSubmit={handleAplicarFiltro}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <MDTypography variant="caption" color={darkMode ? "white" : "text"} sx={{ display: "block", mb: 0.5, fontWeight: "bold" }}>
                Cliente
              </MDTypography>
              <Autocomplete
                options={clientes}
                getOptionLabel={(opt) =>
                  opt ? opt.nomeComum || opt.nomeFantasia || opt.ccNomeComum || opt.ccNomeFantasia || opt.nomeCompleto || opt.ccNome || opt.label || "" : ""
                }
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={filtroCliente}
                onChange={(e, val) => setFiltroCliente(val)}
                renderInput={(params) => (
                  <MDInput {...params} size="small" placeholder="Todos os clientes" fullWidth sx={commonInputSx} />
                )}
                sx={{
                  "& .MuiSvgIcon-root": {
                    color: inputColor,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <MDTypography variant="caption" color={darkMode ? "white" : "text"} sx={{ display: "block", mb: 0.5, fontWeight: "bold" }}>
                Status do Contato
              </MDTypography>
              <Select
                size="small"
                fullWidth
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                sx={commonSelectSx}
              >
                <MenuItem value="TODOS">Todos</MenuItem>
                <MenuItem value="ATIVO">Ativo</MenuItem>
                <MenuItem value="INVALIDO">Inválido / Ruim</MenuItem>
                <MenuItem value="BLACKLIST">Blacklist</MenuItem>
              </Select>
            </Grid>

            {tabIndex === 0 && (
              <Grid item xs={12} sm={6} md={2}>
                <MDTypography variant="caption" color={darkMode ? "white" : "text"} sx={{ display: "block", mb: 0.5, fontWeight: "bold" }}>
                  Tipo de Telefone
                </MDTypography>
                <Select
                  size="small"
                  fullWidth
                  value={filtroTipoTelefone}
                  onChange={(e) => setFiltroTipoTelefone(e.target.value)}
                  displayEmpty
                  sx={commonSelectSx}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="2">Celular</MenuItem>
                  <MenuItem value="1">Fixo</MenuItem>
                  <MenuItem value="3">WhatsApp</MenuItem>
                </Select>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={tabIndex === 0 ? 3 : 5}>
              <MDTypography variant="caption" color={darkMode ? "white" : "text"} sx={{ display: "block", mb: 0.5, fontWeight: "bold" }}>
                Busca Rápida
              </MDTypography>
              <MDInput
                size="small"
                fullWidth
                placeholder={tabIndex === 0 ? "Número, Nome, CPF/CNPJ, Cód. Cliente" : "E-mail, Nome, CPF/CNPJ, Cód. Cliente"}
                value={filtroBusca}
                onChange={(e) => setFiltroBusca(e.target.value)}
                sx={commonInputSx}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={2} display="flex" gap={1}>
              <MDButton variant="gradient" color="info" size="small" type="submit" fullWidth disabled={loading}>
                <Icon>search</Icon>&nbsp;Filtrar
              </MDButton>
              <MDButton
                variant="outlined"
                color={darkMode ? "white" : "secondary"}
                size="small"
                onClick={handleLimparFiltros}
                sx={{
                  color: darkMode ? "#ffffff !important" : undefined,
                  borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined,
                  "&:hover": {
                    borderColor: darkMode ? "#ffffff !important" : undefined,
                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.08) !important" : undefined,
                  },
                }}
              >
                Limpar
              </MDButton>
            </Grid>
          </Grid>
        </form>
      </Card>

      {/* Grid Principal: Tabela de Resultados (8 cols) + Painel de Ação (4 cols) */}
      <Grid container spacing={2.5}>
        {/* Tabela de Contatos Encontrados */}
        <Grid item xs={12} lg={8} xl={8.5}>
          <Card sx={{ backgroundColor: cardBg, p: 2, borderRadius: "12px", border: `1px solid ${borderColor}` }}>
            {/* Header da Tabela */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1.5}>
              <MDBox display="flex" alignItems="center" gap={1.5}>
                <Icon color="info">format_list_bulleted</Icon>
                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                  {tabIndex === 0 ? "Telefones Encontrados" : "E-mails Encontrados"}
                </MDTypography>
                <Chip
                  label={`${data.totalEncontrados.toLocaleString()} registros`}
                  size="small"
                  sx={{
                    backgroundColor: darkMode ? "rgba(33, 150, 243, 0.2)" : "#e3f2fd",
                    color: "#1976d2",
                    fontWeight: "bold",
                    height: "22px",
                  }}
                />
              </MDBox>

              <MDBox display="flex" alignItems="center" gap={1}>
                <MDTypography variant="caption" color="text">Exibir:</MDTypography>
                <Select
                  size="small"
                  value={data.size}
                  onChange={(e) => fetchData(0, parseInt(e.target.value, 10))}
                  sx={{
                    ...commonSelectSx,
                    width: 80,
                    height: 34,
                  }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </MDBox>
            </MDBox>

            {/* Barra de Seleção Rápida */}
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={1}
              mb={1.5}
              borderRadius="8px"
              sx={{ backgroundColor: innerCardBg, border: `1px solid ${borderColor}` }}
            >
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={handleSelectAllFiltered}
                  disabled={data.totalEncontrados === 0}
                  sx={{ fontSize: "0.75rem", py: 0.5 }}
                >
                  <Icon fontSize="small">done_all</Icon>&nbsp;Selecionar todos os resultados ({data.totalEncontrados.toLocaleString()})
                </MDButton>
                {selectedIds.size > 0 && (
                  <MDButton
                    variant="text"
                    color={darkMode ? "white" : "secondary"}
                    size="small"
                    onClick={handleClearSelection}
                    sx={{
                      fontSize: "0.75rem",
                      py: 0.5,
                      color: darkMode ? "#ffffff !important" : undefined,
                    }}
                  >
                    <Icon fontSize="small">clear</Icon>&nbsp;Limpar seleção
                  </MDButton>
                )}
              </MDBox>

              <MDTypography variant="caption" fontWeight="bold" color={totalSelecionadosCount > 0 ? "info" : "text"}>
                {totalSelecionadosCount.toLocaleString()} selecionado(s)
              </MDTypography>
            </MDBox>

            {/* Tabela de Contatos */}
            <TableContainer sx={{ minHeight: 380, maxHeight: 600 }}>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ verticalAlign: "middle", py: 1, pl: 2, pr: 0.5, width: 48 }}>
                      <Checkbox
                        size="small"
                        checked={isPageAllSelected}
                        indeterminate={selectedIds.size > 0 && !isPageAllSelected && !selectAllResults}
                        onChange={(e) => handleSelectPage(e.target.checked)}
                        sx={{
                          p: 0.5,
                          color: darkMode ? "rgba(255, 255, 255, 0.5)" : undefined,
                          "&.Mui-checked": {
                            color: "#1A73E8 !important",
                          },
                          "&.MuiCheckbox-indeterminate": {
                            color: "#1A73E8 !important",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "middle", py: 1, width: 40 }}>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>#</MDTypography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "middle", py: 1 }}>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                        {tabIndex === 0 ? "Telefone" : "E-mail"}
                      </MDTypography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "middle", py: 1 }}>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Cliente</MDTypography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "middle", py: 1 }}>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Devedor</MDTypography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "middle", py: 1 }}>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Tipo</MDTypography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "middle", py: 1 }}>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Data</MDTypography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "middle", py: 1, textAlign: "center" }}>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Status Atual</MDTypography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <CircularProgress color="info" size={32} />
                        <MDTypography variant="caption" display="block" mt={1} color="text">
                          Carregando contatos...
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : data.itens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <Icon sx={{ fontSize: 40, color: "text.secondary" }}>search_off</Icon>
                        <MDTypography variant="button" display="block" mt={1} color="text">
                          Nenhum contato encontrado com os filtros selecionados.
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.itens.map((item, idx) => {
                      const isSelected = selectedIds.has(item.id);
                      return (
                        <TableRow
                          key={item.id}
                          hover
                          selected={isSelected}
                          onClick={() => handleToggleSelect(item.id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell
                            padding="checkbox"
                            sx={{ verticalAlign: "middle", py: 1, pl: 2, pr: 0.5, width: 48 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleSelect(item.id);
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => handleToggleSelect(item.id)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                p: 0.5,
                                color: darkMode ? "rgba(255, 255, 255, 0.5)" : undefined,
                                "&.Mui-checked": {
                                  color: "#1A73E8 !important",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle", py: 1, width: 40 }}>
                            <MDTypography variant="caption" color="text">
                              {data.page * data.size + idx + 1}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle", py: 1 }}>
                            <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                              {item.contato}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle", py: 1 }}>
                            <MDTypography variant="caption" color="text">
                              {item.clienteNome || "-"}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle", py: 1 }}>
                            <MDTypography variant="caption" fontWeight="medium" display="block" color={darkMode ? "white" : "dark"}>
                              {item.devedorNome || "-"}
                            </MDTypography>
                            {item.devedorCodigoCliente && (
                              <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                                Cód: {item.devedorCodigoCliente}
                              </MDTypography>
                            )}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle", py: 1 }}>
                            <MDTypography variant="caption" color="text">
                              {item.tipoDescricao}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle", py: 1 }}>
                            <MDTypography variant="caption" color="text">
                              {item.ultimoContato || "-"}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle", py: 1, textAlign: "center" }}>
                            {item.statusAtual === "Blacklist" ? (
                              <Chip label="Blacklist" size="small" color="error" sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }} />
                            ) : item.statusAtual === "Inválido" ? (
                              <Chip label="Já Inválido" size="small" color="warning" sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }} />
                            ) : (
                              <Chip label="Ativo" size="small" color="success" sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginação */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2} pt={1} borderTop={`1px solid ${borderColor}`}>
              <MDTypography variant="caption" color="text">
                Página {data.page + 1} de {Math.max(1, data.totalPages)} ({data.totalEncontrados.toLocaleString()} registros)
              </MDTypography>
              <Pagination
                count={data.totalPages}
                page={data.page + 1}
                onChange={(e, newPage) => fetchData(newPage - 1)}
                color="info"
                size="small"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: darkMode ? "#ffffff !important" : "#344767 !important",
                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
                    border: darkMode ? "1px solid rgba(255, 255, 255, 0.25)" : "1px solid rgba(0, 0, 0, 0.15)",
                    borderRadius: "6px",
                    minWidth: 28,
                    height: 28,
                    margin: "0 2px",
                    "&:hover": {
                      backgroundColor: darkMode ? "rgba(255, 255, 255, 0.2) !important" : "rgba(0, 0, 0, 0.08) !important",
                      borderColor: darkMode ? "#90caf9 !important" : "#1976d2 !important",
                    },
                    "& .MuiSvgIcon-root": {
                      color: darkMode ? "#ffffff !important" : "inherit",
                      fontSize: "1.2rem !important",
                    },
                  },
                  "& .MuiPaginationItem-icon": {
                    color: darkMode ? "#ffffff !important" : "inherit",
                    fontSize: "1.2rem !important",
                  },
                  "& .Mui-disabled": {
                    opacity: "0.85 !important",
                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.04) !important" : "rgba(0, 0, 0, 0.02) !important",
                    borderColor: darkMode ? "rgba(255, 255, 255, 0.15) !important" : "rgba(0, 0, 0, 0.1) !important",
                    color: darkMode ? "#90caf9 !important" : "rgba(0, 0, 0, 0.38) !important",
                    "& .MuiSvgIcon-root": {
                      color: darkMode ? "#90caf9 !important" : "rgba(0, 0, 0, 0.38) !important",
                      opacity: "0.75 !important",
                    },
                    "& .MuiPaginationItem-icon": {
                      color: darkMode ? "#90caf9 !important" : "rgba(0, 0, 0, 0.38) !important",
                      opacity: "0.75 !important",
                    },
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#1A73E8 !important",
                    color: "#ffffff !important",
                    fontWeight: "bold",
                    borderColor: "#1A73E8 !important",
                  },
                }}
              />
            </MDBox>
          </Card>
        </Grid>

        {/* Painel Lateral: Marcação em Lote */}
        <Grid item xs={12} lg={4} xl={3.5}>
          <Card
            sx={{
              backgroundColor: cardBg,
              p: 2.5,
              borderRadius: "12px",
              border: `1px solid ${borderColor}`,
              position: "sticky",
              top: 20,
            }}
          >
            {/* Header do Painel */}
            <MDBox display="flex" alignItems="center" gap={1.2} mb={1}>
              <Icon color="info">settings</Icon>
              <MDTypography variant="h6" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                Marcação em Lote
              </MDTypography>
            </MDBox>
            <MDTypography variant="caption" color="text" display="block" mb={2}>
              Defina a ação que será aplicada aos contatos selecionados.
            </MDTypography>

            {/* Opções de Ação */}
            <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={1}>
              Ação
            </MDTypography>

            <RadioGroup value={acaoSelecionada} onChange={(e) => setAcaoSelecionada(e.target.value)}>
              {/* Opção 1: Inválido */}
              <MDBox
                p={1.5}
                mb={1.5}
                borderRadius="8px"
                sx={{
                  backgroundColor: acaoSelecionada === "INVALIDO" ? (darkMode ? "rgba(244, 67, 54, 0.15)" : "#ffebee") : innerCardBg,
                  border: acaoSelecionada === "INVALIDO" ? "1px solid #f44336" : `1px solid ${borderColor}`,
                  cursor: "pointer",
                  transition: "all 150ms ease",
                }}
                onClick={() => setAcaoSelecionada("INVALIDO")}
              >
                <FormControlLabel
                  value="INVALIDO"
                  control={
                    <Radio
                      size="small"
                      checked={acaoSelecionada === "INVALIDO"}
                      onChange={() => setAcaoSelecionada("INVALIDO")}
                      sx={{
                        color: darkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.54)",
                        "&.Mui-checked": {
                          color: "#f44336 !important",
                          "& .MuiSvgIcon-root": {
                            borderColor: "#f44336 !important",
                          },
                          "&:after": {
                            opacity: "1 !important",
                            backgroundColor: "#f44336 !important",
                            backgroundImage: "none !important",
                          },
                        },
                      }}
                    />
                  }
                  label={
                    <MDBox>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon color="error" fontSize="small">phone_disabled</Icon>
                        <MDTypography variant="button" fontWeight="bold" color="error">
                          Marcar como Inválido
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.75rem", mt: 0.3 }}>
                        Contato não existe, não atende ou é inválido.
                      </MDTypography>
                    </MDBox>
                  }
                  sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
                />
              </MDBox>

              {/* Opção 2: Blacklist */}
              <MDBox
                p={1.5}
                mb={1.5}
                borderRadius="8px"
                sx={{
                  backgroundColor: acaoSelecionada === "BLACKLIST" ? (darkMode ? "rgba(183, 28, 28, 0.25)" : "#fce4ec") : innerCardBg,
                  border: acaoSelecionada === "BLACKLIST" ? "1px solid #b71c1c" : `1px solid ${borderColor}`,
                  cursor: "pointer",
                  transition: "all 150ms ease",
                }}
                onClick={() => setAcaoSelecionada("BLACKLIST")}
              >
                <FormControlLabel
                  value="BLACKLIST"
                  control={
                    <Radio
                      size="small"
                      checked={acaoSelecionada === "BLACKLIST"}
                      onChange={() => setAcaoSelecionada("BLACKLIST")}
                      sx={{
                        color: darkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.54)",
                        "&.Mui-checked": {
                          color: "#d32f2f !important",
                          "& .MuiSvgIcon-root": {
                            borderColor: "#d32f2f !important",
                          },
                          "&:after": {
                            opacity: "1 !important",
                            backgroundColor: "#d32f2f !important",
                            backgroundImage: "none !important",
                          },
                        },
                      }}
                    />
                  }
                  label={
                    <MDBox>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon color="error" fontSize="small">block</Icon>
                        <MDTypography variant="button" fontWeight="bold" sx={{ color: "#d32f2f" }}>
                          Marcar como Blacklist
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.75rem", mt: 0.3 }}>
                        Contato inibido e proibido de ser utilizado.
                      </MDTypography>
                    </MDBox>
                  }
                  sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
                />
              </MDBox>

              {/* Opção 3: Reativar */}
              <MDBox
                p={1.5}
                mb={2}
                borderRadius="8px"
                sx={{
                  backgroundColor: acaoSelecionada === "REATIVAR" ? (darkMode ? "rgba(76, 175, 80, 0.15)" : "#e8f5e9") : innerCardBg,
                  border: acaoSelecionada === "REATIVAR" ? "1px solid #4caf50" : `1px solid ${borderColor}`,
                  cursor: "pointer",
                  transition: "all 150ms ease",
                }}
                onClick={() => setAcaoSelecionada("REATIVAR")}
              >
                <FormControlLabel
                  value="REATIVAR"
                  control={
                    <Radio
                      size="small"
                      checked={acaoSelecionada === "REATIVAR"}
                      onChange={() => setAcaoSelecionada("REATIVAR")}
                      sx={{
                        color: darkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.54)",
                        "&.Mui-checked": {
                          color: "#4caf50 !important",
                          "& .MuiSvgIcon-root": {
                            borderColor: "#4caf50 !important",
                          },
                          "&:after": {
                            opacity: "1 !important",
                            backgroundColor: "#4caf50 !important",
                            backgroundImage: "none !important",
                          },
                        },
                      }}
                    />
                  }
                  label={
                    <MDBox>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon color="success" fontSize="small">check_circle</Icon>
                        <MDTypography variant="button" fontWeight="bold" color="success">
                          Reativar / Remover Inibição
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.75rem", mt: 0.3 }}>
                        Restabelecer contato como ativo na base de cobrança.
                      </MDTypography>
                    </MDBox>
                  }
                  sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
                />
              </MDBox>
            </RadioGroup>

            {/* Observação */}
            <MDBox mb={2}>
              <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                  Observação (opcional)
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {observacao.length}/500
                </MDTypography>
              </MDBox>
              <MDInput
                multiline
                rows={3}
                placeholder="Digite uma observação para esta marcação em lote..."
                value={observacao}
                onChange={(e) => setObservacao(e.target.value.slice(0, 500))}
                fullWidth
                sx={commonInputSx}
              />
            </MDBox>

            {/* Alerta de Escopo da Ação */}
            <MDBox
              p={1.5}
              mb={2}
              borderRadius="8px"
              display="flex"
              alignItems="flex-start"
              gap={1}
              sx={{ backgroundColor: darkMode ? "rgba(33, 150, 243, 0.15)" : "#e3f2fd" }}
            >
              <Icon color="info" fontSize="small" sx={{ mt: 0.2 }}>info</Icon>
              <MDTypography variant="caption" color={darkMode ? "white" : "text"} sx={{ fontSize: "0.75rem" }}>
                {selectAllResults
                  ? `Esta ação será aplicada em TODOS os ${data.totalEncontrados.toLocaleString()} registros retornados pelo filtro atual.`
                  : `Esta ação será aplicada somente aos ${totalSelecionadosCount.toLocaleString()} contatos selecionados.`}
              </MDTypography>
            </MDBox>

            {/* Botão de Aplicação */}
            <MDButton
              variant="gradient"
              color={acaoSelecionada === "REATIVAR" ? "success" : "info"}
              fullWidth
              size="medium"
              disabled={totalSelecionadosCount === 0 || applyingAction}
              onClick={handleExecuteAction}
              sx={{ py: 1.2, mb: 2.5 }}
            >
              {applyingAction ? (
                <>
                  <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> Processando...
                </>
              ) : (
                <>
                  <Icon>play_arrow</Icon>&nbsp;Aplicar Marcação
                </>
              )}
            </MDButton>

            {/* Resumo da Ação */}
            <MDBox p={1.5} borderRadius="8px" sx={{ backgroundColor: innerCardBg, border: `1px solid ${borderColor}` }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <Icon color="success" fontSize="small">verified</Icon>
                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "0.8rem" }}>
                  Resumo da Ação
                </MDTypography>
              </MDBox>

              <Grid container spacing={0.5} sx={{ fontSize: "0.75rem" }}>
                <Grid item xs={6}>
                  <MDTypography variant="caption" color="text">Contatos selecionados:</MDTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                    {totalSelecionadosCount.toLocaleString()}
                  </MDTypography>
                </Grid>

                <Grid item xs={6}>
                  <MDTypography variant="caption" color="text">Ação:</MDTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color={acaoSelecionada === "REATIVAR" ? "success" : "error"}
                  >
                    {acaoSelecionada === "INVALIDO" ? "Inválido" : acaoSelecionada === "BLACKLIST" ? "Blacklist" : "Reativar"}
                  </MDTypography>
                </Grid>

                <Grid item xs={6}>
                  <MDTypography variant="caption" color="text">Usuário:</MDTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                    {usuarioLogadoNome}
                  </MDTypography>
                </Grid>

                <Grid item xs={12} mt={0.5}>
                  <MDTypography variant="caption" color="text" display="block">Observação:</MDTypography>
                  <MDTypography variant="caption" color={darkMode ? "white" : "dark"} sx={{ fontStyle: "italic" }}>
                    {observacao || "-"}
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* Accordion Inferior: Histórico de Marcações em Lote */}
      <MDBox mt={3}>
        <Accordion
          defaultExpanded={true}
          sx={{
            backgroundColor: cardBg,
            borderRadius: "12px !important",
            border: `1px solid ${borderColor}`,
            boxShadow: "none",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<Icon color="text">expand_more</Icon>}>
            <MDBox display="flex" alignItems="center" gap={1.2}>
              <Icon color="info">history</Icon>
              <MDBox>
                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                  Histórico de Marcações em Lote
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block">
                  Últimas marcações realizadas no sistema
                </MDTypography>
              </MDBox>
            </MDBox>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1 }}><MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Data/Hora</MDTypography></TableCell>
                    <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1 }}><MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Usuário</MDTypography></TableCell>
                    <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1 }}><MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Tipo</MDTypography></TableCell>
                    <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1 }}><MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Ação</MDTypography></TableCell>
                    <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1, textAlign: "right" }}><MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Qtde Enviada</MDTypography></TableCell>
                    <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1, textAlign: "right" }}><MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Qtde Marcada</MDTypography></TableCell>
                    <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1, textAlign: "center" }}><MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Status</MDTypography></TableCell>
                    <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1 }}><MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Observação</MDTypography></TableCell>
                    <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1, textAlign: "center" }}><MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Ações</MDTypography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingHistorico ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} color="info" />
                      </TableCell>
                    </TableRow>
                  ) : historicoLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                        <MDTypography variant="caption" color="text">Nenhum registro de marcação em lote encontrado.</MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    historicoLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1 }}>
                          <MDTypography variant="caption" color="text">{log.dataHora}</MDTypography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1 }}>
                          <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"}>{log.usuario}</MDTypography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1 }}>
                          <Chip
                            label={log.tipoContato === "EMAIL" ? "E-mail" : "Telefone"}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1 }}>
                          <MDTypography variant="caption" fontWeight="bold" color={log.acao === "Reativar" ? "success" : "error"}>
                            {log.acao}
                          </MDTypography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1, textAlign: "right" }}>
                          <MDTypography variant="caption" color="text">{log.qtdeEnviada}</MDTypography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1, textAlign: "right" }}>
                          <MDTypography variant="caption" fontWeight="bold" color="info">{log.qtdeMarcada}</MDTypography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1, textAlign: "center" }}>
                          {log.status === "CONCLUIDO" ? (
                            <Chip label="Concluído" size="small" color="success" sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }} />
                          ) : log.status === "CONCLUIDO_COM_PENDENCIAS" ? (
                            <Chip label="Com pendências" size="small" color="warning" sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }} />
                          ) : (
                            <Chip label="Erro" size="small" color="error" sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1 }}>
                          <MDTypography variant="caption" color="text" sx={{ maxWidth: 180, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {log.observacao || "-"}
                          </MDTypography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", pt: 1.5, pb: 1, textAlign: "center" }}>
                          <Tooltip title="Ver detalhes">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => {
                                setSelectedLog(log);
                                setDetalhesModalOpen(true);
                              }}
                            >
                              <Icon fontSize="small">visibility</Icon>
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </MDBox>

      {/* Modal de Detalhes da Execução do Histórico */}
      <Dialog
        open={detalhesModalOpen}
        onClose={() => setDetalhesModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cardBg,
            color: darkMode ? "#ffffff" : "inherit",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon color="info">receipt_long</Icon>
            <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>
              Detalhes da Execução #{selectedLog?.id}
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={() => setDetalhesModalOpen(false)}>
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <MDTypography variant="caption" color="text">Data/Hora:</MDTypography>
                <MDTypography variant="button" fontWeight="medium" display="block" color={darkMode ? "white" : "dark"}>
                  {selectedLog.dataHora}
                </MDTypography>
              </Grid>
              <Grid item xs={6}>
                <MDTypography variant="caption" color="text">Usuário:</MDTypography>
                <MDTypography variant="button" fontWeight="medium" display="block" color={darkMode ? "white" : "dark"}>
                  {selectedLog.usuario}
                </MDTypography>
              </Grid>

              <Grid item xs={6}>
                <MDTypography variant="caption" color="text">Tipo de Contato:</MDTypography>
                <MDTypography variant="button" fontWeight="medium" display="block" color={darkMode ? "white" : "dark"}>
                  {selectedLog.tipoContato}
                </MDTypography>
              </Grid>
              <Grid item xs={6}>
                <MDTypography variant="caption" color="text">Ação Realizada:</MDTypography>
                <MDTypography variant="button" fontWeight="bold" display="block" color="info">
                  {selectedLog.acao}
                </MDTypography>
              </Grid>

              <Grid item xs={4}>
                <MDTypography variant="caption" color="text">Enviados:</MDTypography>
                <MDTypography variant="button" fontWeight="medium" display="block" color={darkMode ? "white" : "dark"}>
                  {selectedLog.qtdeEnviada}
                </MDTypography>
              </Grid>
              <Grid item xs={4}>
                <MDTypography variant="caption" color="text">Marcados:</MDTypography>
                <MDTypography variant="button" fontWeight="bold" display="block" color="success">
                  {selectedLog.qtdeMarcada}
                </MDTypography>
              </Grid>
              <Grid item xs={4}>
                <MDTypography variant="caption" color="text">Não Marcados:</MDTypography>
                <MDTypography variant="button" fontWeight="bold" display="block" color={selectedLog.qtdeNaoMarcada > 0 ? "error" : "text"}>
                  {selectedLog.qtdeNaoMarcada}
                </MDTypography>
              </Grid>

              <Grid item xs={12} mt={1}>
                <MDTypography variant="caption" color="text">Observação:</MDTypography>
                <MDBox p={1} borderRadius="6px" sx={{ backgroundColor: innerCardBg }}>
                  <MDTypography variant="caption" color={darkMode ? "white" : "dark"}>
                    {selectedLog.observacao || "Nenhuma observação informada."}
                  </MDTypography>
                </MDBox>
              </Grid>

              {selectedLog.detalhesErros && (
                <Grid item xs={12} mt={1}>
                  <MDTypography variant="caption" color="error" fontWeight="bold">Detalhes de Inconsistências / Erros:</MDTypography>
                  <MDBox p={1} borderRadius="6px" sx={{ backgroundColor: innerCardBg, maxHeight: 150, overflowY: "auto" }}>
                    <MDTypography variant="caption" color="error" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                      {selectedLog.detalhesErros}
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color={darkMode ? "white" : "secondary"}
            size="small"
            onClick={() => setDetalhesModalOpen(false)}
            sx={{
              color: darkMode ? "#ffffff !important" : undefined,
              borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined,
              "&:hover": {
                borderColor: darkMode ? "#ffffff !important" : undefined,
                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.08) !important" : undefined,
              },
            }}
          >
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar de Feedback */}
      <MDSnackbar
        color={snackbar.color}
        icon={snackbar.color === "success" ? "check" : snackbar.color === "error" ? "warning" : "info"}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.open}
        onClose={closeSnackbar}
        close={closeSnackbar}
      />
    </MDBox>
  );
}
